import { ThumbnailCore } from "../../core/ui/thumbnail/core.js";
import { listen, observeResize } from "@videojs/utils/dom";
//#region src/dom/ui/thumbnail.ts
function createThumbnail(options) {
	const { getContainer, getImg, onStateChange } = options;
	const core = new ThumbnailCore();
	let loading = false;
	let error = false;
	let naturalWidth = 0;
	let naturalHeight = 0;
	let lastSrc = "";
	let boundImg = null;
	let checkedImg = null;
	let stopListeningToImg = null;
	let stopObservingResize = null;
	const failedSrcs = /* @__PURE__ */ new Set();
	function onImgLoad() {
		const img = getImg();
		if (img) {
			naturalWidth = img.naturalWidth;
			naturalHeight = img.naturalHeight;
		}
		failedSrcs.delete(lastSrc);
		loading = false;
		error = false;
		onStateChange();
	}
	function markFailed() {
		failedSrcs.add(lastSrc);
		loading = false;
		error = true;
	}
	function onImgError() {
		markFailed();
		onStateChange();
	}
	function bindImg(img) {
		stopListeningToImg = new AbortController();
		listen(img, "load", onImgLoad, { signal: stopListeningToImg.signal });
		listen(img, "error", onImgError, { signal: stopListeningToImg.signal });
	}
	function ensureBindings() {
		const img = getImg();
		if (img !== boundImg) {
			stopListeningToImg?.abort();
			stopListeningToImg = null;
			boundImg = img;
			checkedImg = null;
			if (img) bindImg(img);
		}
		if (!stopObservingResize) {
			const container = getContainer();
			if (container) stopObservingResize = observeResize(container, onStateChange);
		}
	}
	function updateSrc(url) {
		ensureBindings();
		const src = url ?? "";
		if (src === lastSrc) return;
		lastSrc = src;
		if (src) {
			const failed = failedSrcs.has(src);
			loading = !failed;
			error = failed;
		} else {
			loading = false;
			error = false;
			naturalWidth = 0;
			naturalHeight = 0;
		}
	}
	function connect() {
		ensureBindings();
		const img = getImg();
		if (!img || img === checkedImg) return;
		checkedImg = img;
		if (!img.complete || !lastSrc) return;
		const previous = {
			loading,
			error,
			naturalWidth,
			naturalHeight
		};
		if (img.naturalWidth > 0) {
			naturalWidth = img.naturalWidth;
			naturalHeight = img.naturalHeight;
			loading = false;
			error = false;
		} else markFailed();
		if (previous.loading !== loading || previous.error !== error || previous.naturalWidth !== naturalWidth || previous.naturalHeight !== naturalHeight) onStateChange();
	}
	function disconnectImg(img) {
		if (img !== boundImg) return;
		stopListeningToImg?.abort();
		stopListeningToImg = null;
		boundImg = null;
		checkedImg = null;
	}
	function destroy() {
		stopListeningToImg?.abort();
		stopListeningToImg = null;
		boundImg = null;
		checkedImg = null;
		stopObservingResize?.();
		stopObservingResize = null;
	}
	return {
		get loading() {
			return loading;
		},
		get error() {
			return error;
		},
		get naturalWidth() {
			return naturalWidth;
		},
		get naturalHeight() {
			return naturalHeight;
		},
		readConstraints() {
			const el = getContainer();
			if (!el) return {
				minWidth: 0,
				maxWidth: Infinity,
				minHeight: 0,
				maxHeight: Infinity
			};
			return core.parseConstraints(getComputedStyle(el));
		},
		updateSrc,
		connect,
		disconnectImg,
		destroy
	};
}
//#endregion
export { createThumbnail };

//# sourceMappingURL=thumbnail.js.map